import type { ScheduleEntry } from '../../types';
import type { ScheduleUser } from '../../components/schedule/scheduleHelpers';
import { businessMinutesIntoDay } from '../../components/schedule/scheduleHelpers';
import { canonicalMoney, addMoney } from '../../lib/money';

// ── Time math ──

export const PX_PER_MIN = 4; // 4px per minute = 60px per 15-min slot = 240px per hour
export const TIME_COL_W = 60;
export const MIN_COL_W = 180;
export const SLOT_INTERVAL = 15; // minutes

export function timeToMinutes(t: string): number {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + (m || 0);
}

export function minutesToTime(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function fmtTime12(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const dh = h % 12 || 12;
  return m === 0 ? `${dh} ${ampm}` : `${dh}:${String(m).padStart(2, '0')} ${ampm}`;
}

export function entryStartMin(entry: ScheduleEntry): number {
  // A2 P4 — position a stored instant in business tz, not device tz.
  return businessMinutesIntoDay(entry.start_at);
}

export function entryEndMin(entry: ScheduleEntry): number {
  return businessMinutesIntoDay(entry.end_at);
}

export function entryDurationMin(entry: ScheduleEntry): number {
  return entryEndMin(entry) - entryStartMin(entry);
}

// ── Position math ──

export function topForMinute(minute: number, gridStartMin: number): number {
  return (minute - gridStartMin) * PX_PER_MIN;
}

export function heightForDuration(durationMin: number): number {
  return durationMin * PX_PER_MIN;
}

// ── Operator status ──

export type OperatorStatus = 'available' | 'in_service' | 'on_break' | 'off_today';

export function getOperatorStatus(
  userId: string,
  entries: ScheduleEntry[],
  nowMin: number,
): OperatorStatus {
  const userEntries = entries.filter(e =>
    e.assigned_to?.includes(userId) && e.status !== 'cancelled',
  );
  if (userEntries.length === 0) return 'off_today';

  for (const e of userEntries) {
    const start = entryStartMin(e);
    const end = entryEndMin(e);
    if (nowMin >= start && nowMin < end) {
      if (e.type === 'event' || e.type === 'personal' || e.type === 'block') return 'on_break';
      return 'in_service';
    }
  }
  return 'available';
}

export const STATUS_DOT_COLORS: Record<OperatorStatus, string> = {
  available: '#34C759',
  in_service: '#0F6CBD',
  on_break: '#FFB800',
  off_today: '#8E8E93',
};

export const STATUS_LABELS: Record<OperatorStatus, string> = {
  available: 'Available',
  in_service: 'In service',
  on_break: 'On break',
  off_today: 'Off today',
};

// ── Gap detection ──

export interface GapSlot {
  operatorId: string;
  startMin: number;
  endMin: number;
}

export function findGaps(
  operatorId: string,
  entries: ScheduleEntry[],
  openMin: number,
  closeMin: number,
  nowMin: number,
  minGapMin: number = 15,
): GapSlot[] {
  const opEntries = entries
    .filter(e => e.assigned_to?.includes(operatorId) && e.status !== 'cancelled')
    .map(e => ({ start: entryStartMin(e), end: entryEndMin(e) }))
    .sort((a, b) => a.start - b.start);

  const gaps: GapSlot[] = [];
  let cursor = Math.max(openMin, nowMin);

  for (const e of opEntries) {
    if (e.start > cursor && e.start - cursor >= minGapMin) {
      gaps.push({ operatorId, startMin: cursor, endMin: e.start });
    }
    cursor = Math.max(cursor, e.end);
  }

  if (closeMin > cursor && closeMin - cursor >= minGapMin) {
    gaps.push({ operatorId, startMin: cursor, endMin: closeMin });
  }

  return gaps;
}

// ── Stats ──

export function computeStats(
  entries: ScheduleEntry[],
  operators: ScheduleUser[],
  openMin: number,
  closeMin: number,
) {
  const bookings = entries.filter(e => e.type === 'booking' && e.status !== 'cancelled');
  const walkIns = bookings.filter(e => e.title?.toLowerCase().includes('walk-in'));
  // Rule #34: canonical money sum via the wrapper. Return as number for
  // downstream consumers that haven't migrated yet (stats are read as numbers).
  const revenueCanonical = bookings.reduce((sum, e) => {
    const match = e.title?.match(/\$(\d+(?:\.\d{2})?)/);
    return match ? addMoney(sum, canonicalMoney(match[1])) : sum;
  }, '0.00');
  const revenue = Number(revenueCanonical);

  const totalAvailableMin = operators.length * (closeMin - openMin);
  const bookedMin = bookings.reduce((sum, e) => sum + entryDurationMin(e), 0);
  const utilization = totalAvailableMin > 0 ? Math.round((bookedMin / totalAvailableMin) * 100) : 0;

  // Count future gaps across all operators
  const now = new Date();
  const nowMin = now.getHours() * 60 + now.getMinutes();
  let gapCount = 0;
  for (const op of operators) {
    gapCount += findGaps(op.id, entries, openMin, closeMin, nowMin).length;
  }

  return {
    bookingCount: bookings.length,
    walkInCount: walkIns.length,
    gapCount,
    revenue,
    utilization,
  };
}

// ── Name hash for avatar gradient ──

const AVATAR_GRADIENTS = [
  ['#6CB4EE', '#4A90D9'], ['#12B76A', '#0E9255'], ['#F59E0B', '#D97706'],
  ['#8B5CF6', '#7C3AED'], ['#EC4899', '#DB2777'], ['#14B8A6', '#0D9488'],
  ['#F97316', '#EA580C'], ['#06B6D4', '#0891B2'], ['#EF4444', '#DC2626'],
  ['#A855F7', '#9333EA'], ['#10B981', '#059669'], ['#F43F5E', '#E11D48'],
];

export function nameGradient(name: string): [string, string] {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_GRADIENTS[Math.abs(h) % AVATAR_GRADIENTS.length] as [string, string];
}

export function initials(name: string): string {
  const parts = name.split(' ');
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
}
